import { isDefined } from "@clipboard-health/util-ts";
import { addMinutes, differenceInMinutes, isAfter, parseISO } from "date-fns";

import { type WorkerTripResource } from "./useGetWorkerTrips";

const STATIONARY_MOVEMENT: NonNullable<WorkerTripResource["attributes"]["latestWorkerMovement"]> =
  "STATIONARY";

export interface FindTripEligibleForEtaNudgeInput {
  trips: WorkerTripResource[];
  stationaryMinutesThresholdForEtaNudge: number;
  shiftStartDateIso: string;
  targetArrivalTimeIso?: string;
  currentDate: Date;
  isLateThresholdMinutes: number;
}

/**
 * Returns the first worker trip that is late, stationary for at least the threshold, and has
 * a valid last movement timestamp.
 *
 * @param params - The trips list, stationary threshold, and optional current date reference.
 */
export function findTripEligibleForEtaNudge({
  trips,
  stationaryMinutesThresholdForEtaNudge,
  isLateThresholdMinutes,
  shiftStartDateIso,
  targetArrivalTimeIso,
  currentDate,
}: FindTripEligibleForEtaNudgeInput): WorkerTripResource | undefined {
  if (trips.length === 0) {
    return undefined;
  }

  return trips.find((trip) => {
    const { attributes } = trip;

    const { currentEtaDuration } = attributes;

    const anchorDate = parseISO(
      attributes.workerDeclaredEta ?? targetArrivalTimeIso ?? shiftStartDateIso
    );
    const predictedArrival = addMinutes(currentDate, currentEtaDuration);
    const isLate = isAfter(predictedArrival, addMinutes(anchorDate, isLateThresholdMinutes));

    if (!isLate) {
      return false;
    }

    if (attributes.latestWorkerMovement !== STATIONARY_MOVEMENT) {
      return false;
    }

    if (!isDefined(attributes.latestWorkerMovementTransitionedAt)) {
      return false;
    }

    const minutesStationary = differenceInMinutes(
      currentDate,
      parseISO(attributes.latestWorkerMovementTransitionedAt)
    );

    return minutesStationary >= stationaryMinutesThresholdForEtaNudge;
  });
}
